/* eslint-disable import/max-dependencies */
/* eslint-disable max-lines */
import React, { useState } from 'react';
import invert from 'lodash/invert';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import { useTranslate } from '@im/react-localization';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { isEmbedded } from 'src/util';
import { EDStatus, EDStatusCode } from '../hooks/exceptionDeal';
import { VendorLookUpResponse } from 'src/hooks/vendor-list';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { DatePickerProvider } from 'src/provider/DatePickerProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

export default function Filters(
  props: Readonly<{
    businessType?: string | null;
    dateType: string;
    dateValues:
      | {
          maxuploadDate?: string;
          minuploadDate?: string;
          uploadDate?: string;
        }
      | undefined;
    filters: string;
    handleClose: () => void;
    role: boolean;
    setDateType: React.Dispatch<React.SetStateAction<string>>;
    setDateValues: React.Dispatch<
      React.SetStateAction<
        | {
            maxuploadDate?: string;
            minuploadDate?: string;
            uploadDate?: string;
          }
        | undefined
      >
    >;
    setFilters: React.Dispatch<React.SetStateAction<string>>;
    setPageSetup: React.Dispatch<
      React.SetStateAction<{
        page: number;
        pageSize: number;
      }>
    >;
    setVendors: React.Dispatch<React.SetStateAction<string>>;
    vendorList: VendorLookUpResponse[] | undefined;
    vendors: string;
  }>
) {
  const translate = useTranslate();
  const [filters, setFilters] = useState<number[]>(
    props.filters === '' ? [] : props.filters.split(',').map(Number)
  );
  const [vendors, setVendors] = useState<number[]>(
    props.vendors === '' ? [] : props.vendors.split(',').map(Number)
  );
  const [dateRange, setDateRange] = useState<{
    maxuploadDate: string;
    minuploadDate: string;
  }>({
    maxuploadDate: props.dateValues?.maxuploadDate ?? '',
    minuploadDate: props.dateValues?.minuploadDate ?? '',
  });
  let statusArr = props.role
    ? [
        EDStatus.ESCALATED,
        EDStatus.PENDING_REVIEW,
        EDStatus.NEW,
        EDStatus.NEW_RESELLER,
        EDStatus.NEW_ENDUSER,
        EDStatus.IN_QUEUE,
        EDStatus.IN_PROCESS,
        EDStatus.PENDING_INCENTIVE,
        EDStatus.NEW_PENDING_SERVICE_PLAN,
      ]
    : [
        EDStatus.ESCALATED,
        EDStatus.REJECTED,
        EDStatus.NEW,
        EDStatus.NEW_RESELLER,
        EDStatus.NEW_ENDUSER,
        EDStatus.PENDING_REVIEW,
        EDStatus.APPROVED,
        EDStatus.IN_QUEUE,
        EDStatus.IN_PROCESS,
        EDStatus.PENDING_INCENTIVE,
        EDStatus.NEW_PENDING_SERVICE_PLAN,
      ];

  // Remove 'Pending Incentive Selection' if businessType is 'Subscription'
  if (props.businessType === 'Subscription') {
    statusArr = statusArr.filter(
      (status) => status !== EDStatus.PENDING_INCENTIVE
    );
  }

  const statusList = statusArr.map((value) => ({
    label: translate(
      props.role
        ? `ExceptionDeals.StatusAdmin${invert(EDStatus)[value]}`
        : `ExceptionDeals.StatusOther${invert(EDStatus)[value]}`
    ),
    value,
  }));

  const handleStatusOnChecked = (status: string) => {
    filters.push(EDStatusCode[status as keyof typeof EDStatusCode]);
    props.setPageSetup({ page: 1, pageSize: 10 });
    props.setFilters(filters.toString());
  };
  const handleStatusUnChecked = (status: string) => {
    filters.splice(
      filters.indexOf(EDStatusCode[status as keyof typeof EDStatusCode]),
      1
    );
    props.setPageSetup({ page: 1, pageSize: 10 });
    props.setFilters(filters.toString());
  };
  const handleVendoerOnChecked = (vendor: VendorLookUpResponse) => {
    vendors.push(vendor.vendorID);
    props.setPageSetup({ page: 1, pageSize: 10 });
    props.setVendors(vendors.toString());
  };
  const handleVendoerUnChecked = (vendor: VendorLookUpResponse) => {
    vendors.splice(vendors.indexOf(vendor.vendorID), 1);
    props.setPageSetup({ page: 1, pageSize: 10 });
    props.setVendors(vendors.toString());
  };
  const handleOnDateChange = (name: string, value: string) => {
    props.setPageSetup({ page: 1, pageSize: 10 });
    if (props.dateType === 'range') {
      if (name === 'starts') {
        setDateRange({
          maxuploadDate: '',
          minuploadDate: value,
        });
      } else {
        setDateRange({
          ...dateRange,
          maxuploadDate: value,
        });
        if (dateRange.minuploadDate) {
          props.setDateValues({
            maxuploadDate: value,
            minuploadDate: dateRange.minuploadDate,
          });
        }
      }
    } else {
      props.setDateValues({ uploadDate: value });
    }
  };
  return (
    <Drawer
      anchor={'right'}
      data-testid="gds-vp-ed-list-table-filter-drawer"
      onClose={props.handleClose}
      open={true}
      sx={{
        '.MuiDrawer-paperAnchorRight': {
          height: isEmbedded() ? '100vh' : `calc(100vh - 50px)`,
          top: isEmbedded() ? 0 : 50,
          width: 400,
        },
      }}
    >
      <Box
        data-testid="gds-vp-ed-list-table-filter-header"
        paddingBottom={1}
        paddingTop={2}
        paddingX={2}
      >
        <Stack alignItems={'center'} direction="row">
          <Typography
            sx={(theme) => ({
              fontSize: theme.typography.body1.fontSize,
            })}
            variant="smalltextsemibold"
          >
            {translate('Common.Filters')}
          </Typography>
          <Box flex={1} />
          <Button
            data-testid="gds-vp-ed-list-table-filter-clearall-btn"
            onClick={() => {
              props.setFilters('');
              props.setVendors('');
              props.setDateValues(undefined);
              props.setDateType('date');
              setFilters([]);
              setVendors([]);
              setDateRange({ maxuploadDate: '', minuploadDate: '' });
            }}
            sx={(theme) => ({
              fontSize: theme.typography.body2.fontSize,
              minWidth: 0,
              padding: theme.spacing(0.5, 1),
            })}
          >
            {translate('Common.ClearAll')}
          </Button>
          <IconButton
            data-testid="gds-vp-ed-list-table-filter-close-btn"
            onClick={props.handleClose}
            size="small"
            sx={{ marginLeft: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
      <Divider />
      <Stack
        px={2}
        sx={{ '.MuiAccordionSummary-root': { height: '40px !important' } }}
      >
        {/* Vendors Filter Accordion - only show if not Subscription */}
        {props.businessType !== 'Subscription' && (
          <Accordion defaultExpanded={true}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content.Mui-expanded': {
                  pb: '0.5rem !important',
                },
              }}
            >
              <Typography>{translate('VendorProgram.Vendors')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ height: '210px' }}>
              {props.vendorList?.map((vendor: VendorLookUpResponse) => (
                <div key={vendor.vendorID}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={vendors.includes(vendor.vendorID)}
                        onChange={(event, checked) =>
                          checked
                            ? handleVendoerOnChecked(vendor)
                            : handleVendoerUnChecked(vendor)
                        }
                      />
                    }
                    data-testid={`gds-vp-ed-list-table-filter-label-${vendor.vendorName}`}
                    key={vendor.vendorID}
                    label={
                      <Typography sx={{ fontSize: '14px' }}>
                        {vendor.vendorName}
                      </Typography>
                    }
                    value={vendor.vendorName}
                  />
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
        {/* Status and Date filters remain unchanged... */}
        <Accordion defaultExpanded={true}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={(theme) => ({
              '& .MuiAccordionSummary-content.Mui-expanded': {
                pb: `${theme.spacing(1)} !important`,
              },
            })}
          >
            <Typography>
              {translate('ExceptionDeals.ApprovalStatus')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ height: '210px' }}>
            {Object.keys(statusList).map((status: any) => (
              <div key={status}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.includes(
                        EDStatusCode[statusList[status].value]
                      )}
                      onChange={(event, checked) =>
                        checked
                          ? handleStatusOnChecked(statusList[status].value)
                          : handleStatusUnChecked(statusList[status].value)
                      }
                    />
                  }
                  data-testid={`gds-vp-ed-list-table-filter-label-${statusList[status].value}`}
                  key={status}
                  label={
                    <Typography
                      sx={(theme) => ({
                        fontSize: theme.typography.body2.fontSize,
                        lineHeight: theme.typography.body2.lineHeight,
                      })}
                    >
                      {statusList[status].label}
                    </Typography>
                  }
                  value={status}
                />
              </div>
            ))}
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded={true}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={(theme) => ({
              '& .MuiAccordionSummary-content.Mui-expanded': {
                pb: `${theme.spacing(1)} !important`,
              },
            })}
          >
            <Typography>{translate('VendorProgram.UploadDate')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack
              data-testid={`gds-vp-advanced-search-settings-widget-item`}
              direction="row"
              spacing={1.5}
            >
              <TextField
                fullWidth
                helperText={translate('Common.Condition')}
                name="operator"
                onChange={(event) => props.setDateType(event.target.value)}
                select
                value={props.dateType}
              >
                {[
                  { label: translate('Common.Is'), value: 'date' },
                  { label: translate('Common.Range'), value: 'range' },
                ].map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Typography variant="bodycopyregular">
                      {opt.label}
                    </Typography>
                  </MenuItem>
                ))}
              </TextField>
              {props.dateType === 'date' && (
                <DatePickerProvider>
                  <DatePicker
                    onChange={(value) => {
                      handleOnDateChange(
                        'value',
                        dayjs(value).format('YYYY-MM-DD')
                      );
                    }}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        fullWidth
                        helperText={translate('Common.Value')}
                        name="value"
                      />
                    )}
                    value={props.dateValues?.uploadDate ?? ''}
                  />
                </DatePickerProvider>
              )}
              {props.dateType === 'range' && (
                <DatePickerProvider>
                  <DatePicker
                    onChange={(value) =>
                      handleOnDateChange(
                        'starts',
                        dayjs(value).format('YYYY-MM-DD')
                      )
                    }
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        fullWidth
                        helperText={translate('Common.Start')}
                        name="starts"
                        value={dateRange.minuploadDate}
                      />
                    )}
                    value={dateRange.minuploadDate}
                  />
                  <DatePicker
                    minDate={dateRange.minuploadDate}
                    onChange={(value) =>
                      handleOnDateChange(
                        'ends',
                        dayjs(value).format('YYYY-MM-DD')
                      )
                    }
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        fullWidth
                        helperText={translate('Common.End')}
                        name="ends"
                        value={dateRange.maxuploadDate}
                      />
                    )}
                    value={dateRange.maxuploadDate}
                  />
                </DatePickerProvider>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Drawer>
  );
}
